
import VueApp from '@/@types/app/VueApp';
import FindUserGroupDialog from '@/components/layout/dialogs/FindUserGroupDialog.vue';
import ComponentLoader from '@/components/layout/shared/loaders/ComponentLoader.vue';
import GroupProfilePhoto from '@/components/layout/shared/ProfilePhoto/GroupProfilePhoto.vue';
import UserProfilePhoto from '@/components/layout/shared/ProfilePhoto/UserProfilePhoto.vue';
import { COPY_LINK } from '@/events';
import helper from '@/helper';
import { Article } from '@/models/article/Article';
import apiService from '@/services/ApiService';
import sharedArticleService from '@/services/article/SharedArticleService';
import notificationService from '@/services/NotificationService';
import workspaceService from '@/services/workspace/WorkspaceService';
import { commitSetWorkspaceViewArticles } from '@/store/commits/workspaceViewCommits';
import { Component, Prop } from 'vue-property-decorator';

@Component({
  components: { ComponentLoader, GroupProfilePhoto, UserProfilePhoto, FindUserGroupDialog },
})
export default class ArticleShareSettings extends VueApp {
  @Prop(String) accessType: string;
  @Prop() article: Article;
  @Prop() visible: boolean;
  @Prop() isAllowEditAndDeleteArticle: boolean;

  sharedUsersGroups: any[] = [];
  shareToAnyoneToken: string | null = null;

  get isSharedToAnyone(): boolean {
    return !!this.shareToAnyoneToken;
  }

  get shareAnyoneUrl(): string | null {
    if (!this.shareToAnyoneToken) return null;
    const baseUrl = window.location.origin;
    return `${baseUrl}/#/b/shared/${this.shareToAnyoneToken}`;
  }

  get isSharedUsersGroups(): boolean {
    return this.sharedUsersGroups.length > 0;
  }

  get isShared(): boolean {
    return this.isSharedToAnyone || this.isSharedUsersGroups;
  }

  get isGuest(): boolean {
    return this.$auth.isGuest();
  }

  async toggleShareSettings(isRefreshToken = false) {
    if (isRefreshToken && !this.isSharedToAnyone) {
      notificationService.warning('Enable sharing to generate a new link.');
      return;
    }

    try {
      const data = await sharedArticleService.handleToggleSharedStatus(
        this.article.id,
        this.isSharedToAnyone,
        isRefreshToken,
        false
      );

      this.shareToAnyoneToken = data?.token || null;

      this.handleArticleSharedStatus();

      if (isRefreshToken) {
        notificationService.success('Public link has been refreshed.');
      } else {
        const message = this.isSharedToAnyone ? 'Public link has been enabled.' : 'Public link has been disabled.';
        notificationService.success(message);
      }
    } catch {
      notificationService.error('Failed to update sharing settings');
    }
  }

  async loadShareToAnyoneToken() {
    try {
      const { data } = await apiService.sharedArticle.get(this.article.id);
      this.shareToAnyoneToken = data?.sharedData?.token || null;
    } catch (error) {
      this.shareToAnyoneToken = null;
    }
  }

  handleCopyUrl(forceCopy = false) {
    if (this.isAllowEditAndDeleteArticle && !forceCopy) {
      return;
    }

    if (this.shareAnyoneUrl) {
      helper.copyToClipboard(this.shareAnyoneUrl);
      notificationService.success('Public link copied to clipboard');
    } else {
      notificationService.warning('Public link is not enabled for this article.');
    }
  }

  async handleRemoveEntityFromShared(entityId: string) {
    try {
      await sharedArticleService.handleRemoveEntityFromShared(this.article.id, entityId);
      this.sharedUsersGroups = this.sharedUsersGroups.filter((entity) => entity.id !== entityId);
      this.handleArticleSharedStatus();
    } catch {
      notificationService.error('Failed to remove shared user or group from article');
    }
  }

  async handleAddEntityToShared(entities: any[]) {
    try {
      if (!entities.length) return;
      await sharedArticleService.handleAddEntityToShared(this.article.id, entities);
      await this.loadArticleSharedEntities();
      this.handleArticleSharedStatus();
    } catch {
      notificationService.error('Failed to share article with selected entities');
    }
  }

  async loadArticleSharedEntities() {
    try {
      const { data } = await apiService.sharedArticle.getArticleSharedEntities(this.article.id);
      this.sharedUsersGroups = data || [];
    } catch {
      notificationService.error('Failed to load shared users and groups for article');
    }
  }

  handleArticleSharedStatus() {
    const isShared = this.isSharedToAnyone || this.isSharedUsersGroups;
    const articleItems = workspaceService.workspaceView.articles;
    const updatedItems = articleItems.map((item) => {
      if (item.id !== this.article.id) return item;
      item.isShared = isShared;
      return item;
    });
    commitSetWorkspaceViewArticles(updatedItems);
  }

  handleCopyLink() {
    this.$emit(COPY_LINK);
  }

  mounted() {
    if (this.isGuest) {
      return;
    }
    this.loadShareToAnyoneToken();
    if (this.isAllowEditAndDeleteArticle) {
      this.loadArticleSharedEntities();
    }
  }
}
